import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { User, Mail, Lock, MessageCircle, ShieldCheck, KeyRound, Smartphone } from "lucide-react";
import { toast } from "sonner";

/** OTP modal helper: requests an OTP for the current admin and returns {challenge_id, code} */
function OtpDialog({ open, title, onClose, onConfirm, isEN }) {
  const [challengeId, setChallengeId] = useState(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    const run = async () => {
      try {
        // Re-trigger an OTP by login-style request bound to the current user.
        const { data } = await api.post("/auth/admin/profile-otp");
        if (!active) return;
        setChallengeId(data.challenge_id);
        toast.success(isEN ? "OTP code sent to Telegram" : "Codice OTP inviato su Telegram");
      } catch (e) {
        if (!active) return;
        toast.error(formatApiError(e.response?.data?.detail) || e.message);
        onClose();
      }
    };
    run();
    return () => { active = false; };
  }, [open, isEN, onClose]);

  // Reset local state whenever the dialog re-opens (via key in parent or unmount).
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 max-w-md w-full space-y-4">
        <h3 className="font-display text-xl font-bold flex items-center gap-2"><Smartphone className="w-5 h-5 text-[#FFD700]" /> {title}</h3>
        <p className="text-sm text-zinc-400">{isEN ? "Enter the 6-digit code we sent to your Telegram chat to confirm this action." : "Inserisci il codice a 6 cifre che ti abbiamo inviato su Telegram per confermare."}</p>
        <Input value={code} onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
               className="bg-zinc-950 border-zinc-800 text-center text-2xl tracking-[0.5em] font-mono"
               placeholder="000000" data-testid="profile-otp-input" />
        <div className="flex gap-2 justify-end">
          <Button variant="ghost" onClick={onClose} className="text-zinc-400">{isEN ? "Cancel" : "Annulla"}</Button>
          <Button onClick={async () => {
              setLoading(true);
              try { await onConfirm(challengeId, code); }
              finally { setLoading(false); }
            }}
            disabled={!challengeId || code.length !== 6 || loading}
            className="bg-[#FFD700] hover:bg-[#FACC15] text-black font-semibold"
            data-testid="profile-otp-confirm">
            {loading ? "..." : (isEN ? "Confirm" : "Conferma")}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AdminProfile() {
  const { i18n } = useTranslation();
  const isEN = i18n.language === "en";
  const { user: ctxUser, refresh } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [savingName, setSavingName] = useState(false);

  const [newEmail, setNewEmail] = useState("");
  const [emailCurPw, setEmailCurPw] = useState("");
  const [emailOtpOpen, setEmailOtpOpen] = useState(false);

  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [pwOtpOpen, setPwOtpOpen] = useState(false);

  const [linkChatId, setLinkChatId] = useState("");

  const load = () => api.get("/admin/profile").then((r) => {
    setProfile(r.data);
    setDisplayName(r.data.display_name || "");
  });
  useEffect(() => { load(); }, []);

  if (!profile) return <div className="text-zinc-400">Loading…</div>;

  const saveName = async () => {
    setSavingName(true);
    try {
      await api.patch("/admin/profile", { display_name: displayName });
      await refresh?.();
      toast.success(isEN ? "Name updated" : "Nome aggiornato");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSavingName(false); }
  };

  const submitEmailChange = async (challengeId, code) => {
    try {
      await api.patch("/admin/profile/email", {
        current_password: emailCurPw,
        new_email: newEmail,
        otp_challenge_id: challengeId,
        otp_code: code,
      });
      toast.success(isEN ? "Email updated — please log in again" : "Email aggiornata — accedi di nuovo");
      setEmailOtpOpen(false);
      navigate("/login");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const submitPwChange = async (challengeId, code) => {
    try {
      await api.patch("/admin/profile/password", {
        current_password: curPw,
        new_password: newPw,
        otp_challenge_id: challengeId,
        otp_code: code,
      });
      toast.success(isEN ? "Password updated — please log in again" : "Password aggiornata — accedi di nuovo");
      setPwOtpOpen(false);
      navigate("/login");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const linkTg = async () => {
    if (!linkChatId) return;
    try {
      await api.post("/admin/profile/link-telegram", { chat_id: parseInt(linkChatId, 10) });
      toast.success(isEN ? "Telegram linked" : "Telegram collegato");
      setLinkChatId("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const unlinkTg = async () => {
    if (!window.confirm(isEN ? "Unlink Telegram? You will not receive notifications nor recovery codes." : "Scollegare Telegram? Non riceverai più notifiche né codici di recupero.")) return;
    try {
      await api.delete("/admin/profile/link-telegram");
      toast.success(isEN ? "Telegram unlinked" : "Telegram scollegato");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="space-y-8 max-w-3xl" data-testid="admin-profile-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#FFD700] mb-1">{isEN ? "Account" : "Account"}</p>
        <h1 className="font-display text-4xl font-bold tracking-tight flex items-center gap-2">
          <User className="w-7 h-7 text-[#FFD700]" /> {profile.display_name}
        </h1>
        <p className="text-zinc-400 text-sm mt-1">
          {profile.email} · <span className="text-[#FFD700]">Admin</span>
        </p>
      </div>

      {/* Display name */}
      <Section title={isEN ? "Display name" : "Nome visualizzato"} icon={User}>
        <p className="text-xs text-zinc-500 mb-2">{isEN ? "Shown in the sidebar and Telegram messages." : "Mostrato nel menu e nei messaggi Telegram."}</p>
        <div className="flex gap-2">
          <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                 className="bg-zinc-950 border-zinc-800" data-testid="profile-display-name" />
          <Button onClick={saveName} disabled={savingName || !displayName} className="bg-[#FFD700] hover:bg-[#FACC15] text-black font-semibold">
            {savingName ? "..." : (isEN ? "Save" : "Salva")}
          </Button>
        </div>
      </Section>

      {/* Email change */}
      <Section title={isEN ? "Change email" : "Cambia email"} icon={Mail}>
        <p className="text-xs text-zinc-500 mb-3">{isEN ? "Requires current password and Telegram OTP." : "Richiede password attuale e OTP Telegram."}</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-zinc-400">{isEN ? "New email" : "Nuova email"}</Label>
            <Input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1" data-testid="profile-new-email" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">{isEN ? "Current password" : "Password attuale"}</Label>
            <Input type="password" value={emailCurPw} onChange={(e) => setEmailCurPw(e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1" data-testid="profile-email-curpw" />
          </div>
        </div>
        <Button onClick={() => setEmailOtpOpen(true)} disabled={!newEmail || !emailCurPw}
                className="mt-3 bg-zinc-900 border border-zinc-700 hover:bg-zinc-800 text-zinc-100" data-testid="profile-email-submit">
          {isEN ? "Continue → OTP" : "Continua → OTP"}
        </Button>
      </Section>

      {/* Password change */}
      <Section title={isEN ? "Change password" : "Cambia password"} icon={Lock}>
        <p className="text-xs text-zinc-500 mb-3">{isEN ? "Requires current password and Telegram OTP." : "Richiede password attuale e OTP Telegram."}</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-zinc-400">{isEN ? "Current password" : "Password attuale"}</Label>
            <Input type="password" value={curPw} onChange={(e) => setCurPw(e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1" data-testid="profile-curpw" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">{isEN ? "New password (min 8)" : "Nuova password (min 8)"}</Label>
            <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1" data-testid="profile-newpw" />
          </div>
        </div>
        <Button onClick={() => setPwOtpOpen(true)} disabled={!curPw || newPw.length < 8}
                className="mt-3 bg-zinc-900 border border-zinc-700 hover:bg-zinc-800 text-zinc-100" data-testid="profile-pw-submit">
          {isEN ? "Continue → OTP" : "Continua → OTP"}
        </Button>
      </Section>

      {/* Telegram link */}
      <Section title={isEN ? "Telegram link (2FA + recovery)" : "Collegamento Telegram (2FA + recupero)"} icon={MessageCircle}>
        {profile.telegram_chat_id ? (
          <div className="flex items-center justify-between bg-green-500/[0.06] border border-green-500/30 rounded-lg p-3">
            <div className="text-sm">
              <p className="text-green-300 font-semibold">{isEN ? "Linked" : "Collegato"}</p>
              <p className="text-zinc-400 text-xs">Chat ID: <span className="font-mono">{profile.telegram_chat_id}</span></p>
            </div>
            <Button variant="outline" onClick={unlinkTg} className="bg-red-500/10 border-red-500/30 text-red-300 hover:bg-red-500/20" data-testid="profile-tg-unlink">
              {isEN ? "Unlink" : "Scollega"}
            </Button>
          </div>
        ) : (
          <>
            <p className="text-xs text-zinc-500 mb-3">
              {isEN
                ? "1) Open the Rent Lupi Telegram bot, send /start. 2) On the Dashboard click 'Register chat' (if not yet). 3) Find your chat ID under 'Telegram subscribers' and paste it here."
                : "1) Apri il bot Telegram Rent Lupi e invia /start. 2) In Dashboard clicca 'Registra chat' (se non l'hai già fatto). 3) Trova il tuo chat ID tra i sottoscrittori e incollalo qui."}
            </p>
            <div className="flex gap-2">
              <Input type="number" placeholder="Chat ID" value={linkChatId}
                     onChange={(e) => setLinkChatId(e.target.value)} className="bg-zinc-950 border-zinc-800" data-testid="profile-tg-chatid" />
              <Button onClick={linkTg} disabled={!linkChatId} className="bg-[#FFD700] hover:bg-[#FACC15] text-black font-semibold" data-testid="profile-tg-link">
                {isEN ? "Link" : "Collega"}
              </Button>
            </div>
          </>
        )}
        <p className="text-[11px] text-zinc-600 mt-3 leading-relaxed">
          {isEN
            ? "When linked: you receive ALL admin notifications and password recovery codes (sub-admins won't receive notifications). Required for password recovery in case of credential theft."
            : "Quando collegato: ricevi TUTTE le notifiche admin e i codici di recupero password (i sub-admin non ricevono notifiche). Necessario per il recupero password in caso di furto credenziali."}
        </p>
      </Section>

      <OtpDialog
        open={emailOtpOpen}
        title={isEN ? "Confirm email change" : "Conferma cambio email"}
        isEN={isEN}
        onClose={() => setEmailOtpOpen(false)}
        onConfirm={submitEmailChange}
      />
      <OtpDialog
        open={pwOtpOpen}
        title={isEN ? "Confirm password change" : "Conferma cambio password"}
        isEN={isEN}
        onClose={() => setPwOtpOpen(false)}
        onConfirm={submitPwChange}
      />
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="border border-zinc-800 rounded-xl p-5 bg-zinc-900/30">
      <h2 className="font-display text-xl font-bold mb-4 flex items-center gap-2">
        <Icon className="w-5 h-5 text-[#FFD700]" /> {title}
      </h2>
      {children}
    </div>
  );
}
